/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DTypeForm } from '@grupo-constant/next-dtype/form';
import { DType, DTypeCol, DTypeRepository, DTypeStatus, DTypeToGoLiveRequest, NextLocator } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { cloneDeep } from 'lodash-es';
import { catchError, firstValueFrom, map, Subject, Subscription, takeUntil, throwError } from 'rxjs';
import { FormService } from '../form.service';
import { Form } from '../form.types';
import { ConfirmChanges, UserNodePrivileges } from '../../../modules/admin/projects/projects.types';
import { ChangeDTypeRepositoryNameBody, DTypeAlerts } from '../../dtype/dtype.types';
import { DialogsService } from '../../../dialogs/dialogs.service';
import { DTypeService } from '../../dtype/dtype.service';
import { Events } from '../../events/events.service';
import { FuseConfirmationService } from '../../../../@fuse/services/confirmation/confirmation.service';
import { LocalizePipe } from '../../pipes/localize.pipe';
import { ProjectsService } from '../../../modules/admin/projects/projects.service';
import { UserService } from '../../user/user.service';
import { RequestGoLiveBody, RequestGoLiveDialogData } from '../../../dialogs/request-go-live-dialog/request-go-live-dialog.types';

@Component({
    selector: 'form-design',
    templateUrl: './form-design.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormDesignComponent implements OnInit, OnDestroy, ConfirmChanges {

    form: Form;

    params: Params;
    uri: string;
    dtypeLocator: NextLocator;
    myLocator: NextLocator;
    repo: DTypeRepository;
    role: UserNodePrivileges;
    equalSQLTableModel: boolean;
    equalDType: boolean;
    cancelSave: boolean;

    databaseViewMode: boolean;

    dtypeForm: DTypeForm;
    hasChanges = false;
    loading = true;
    isNew = false;
    hasLiveRelease = false;
    oldDTypeName: string;
    selectedTab: number = 0;

    updateFieldNameSubscription: Subscription;

    showAlerts: DTypeAlerts = {
        showEvened: true,
        showRequestToGoLive: true
    };

    private _unsubscribeAll: Subject<void> = new Subject<void>();

    constructor(
        private activatedRoute: ActivatedRoute,
        private _changeDetectorRef: ChangeDetectorRef,
        private dialogsService: DialogsService,
        private dtypeService: DTypeService,
        private events: Events,
        private _fuseConfirmationService: FuseConfirmationService,
        private localize: LocalizePipe,
        private formService: FormService,
        private projectsService: ProjectsService,
        private router: Router,
        private _snackbar: MatSnackBar,
        private translocoService: TranslocoService,
        private userService: UserService
    ) { }

    ngOnInit() {
        this.activatedRoute.queryParams
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(async (params) => {
                // Viene de otra pagina de design con cambios
                if (this.hasChanges && params && params.uri !== this.uri) {
                    params.uri = this.uri;
                    if (!await this.confirm()) {
                        this.router.navigate(['../design'], {
                            relativeTo: this.activatedRoute,
                            queryParams: params
                        });
                        return;
                    }
                } else if (params && this.uri && params.uri === this.uri) {
                    return;
                }

                this.databaseViewMode = params.databaseViewMode ?? false;
                this.params = params;
                this.hasChanges = false;
                this.loading = true;

                this.role = await this.projectsService.getRoleByLocators(params);
                if (!this.hasPermissionToDesignRepository()) {
                    this.router.navigateByUrl('/403');
                }

                if (params.uri && params.locator) {
                    this.uri = params.uri;
                    this.myLocator = this.formService.getLocatorFromString(params.locator);
                    this.dtypeLocator = this.formService.getLocatorFromString(this.uri.substring(7, this.uri.lastIndexOf('/'))); //form://
                    this.hasLiveRelease = !!(await this.formService.retrieveFormDTypeByUri(this.uri, DTypeStatus.LIVE, this.dtypeLocator));
                    this.repo = cloneDeep(await firstValueFrom(this.formService.retrieveFormDTypeRepositoryByUri(this.uri, DTypeStatus.DESIGN, this.dtypeLocator)));
                    if (this.repo?.locked && this.repo?.dtypeToGoLiveRequest) {
                        this.router.navigateByUrl('/403');
                    }
                    this.oldDTypeName = (cloneDeep(this.repo).payload as Form).name;
                    this.form = this.repo.payload as Form;
                } else if (params.locator) {
                    this.isNew = true;
                    this.myLocator = this.formService.getLocatorFromString(params.locator);
                    this.dtypeLocator = this.formService.getLocatorFromString(params.locator);

                    const dialog = this.dialogsService.openNewFormDialog();
                    const typology = await firstValueFrom(dialog.afterClosed());
                    if (typology) {
                        const reply = await firstValueFrom(this.formService.retrieveNewFormInstance({
                            locator: this.myLocator,
                            typology,
                        }));
                        this.form = reply.form;
                        this.form.uri = 'form://';
                    } else {
                        this.goBack();
                    }
                } else {
                    this.dialogsService.openErrorDialog();
                    this.router.navigate(['../'], { queryParams: { force: true } });
                    return;
                }
                this.formService.updateColumns(this.form.column);

                this.dtypeForm = await this.formService.retrieveFormDtypeForm(this.form, { isUpdate: !this.isNew });
                this.dtypeForm.control.valueChanges
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => {
                        this.hasChanges = true;
                    });

                this._changeDetectorRef.detectChanges();
                this.loading = false;
            });

        this.updateFieldNameSubscription = this.events.subscribe('updateFieldName', (data: { oldName: string; newName: string }) => {
            const fieldsForSummary: string[] = this.dtypeForm.control.get('fieldsForSummary')?.value;
            if (fieldsForSummary?.length) {
                const index = fieldsForSummary.findIndex(i => i === data.oldName);
                if (index > -1) {
                    if (data.newName) {
                        fieldsForSummary[index] = data.newName;
                    } else {
                        fieldsForSummary.splice(index, 1);
                    }
                    this.dtypeForm.control.get('fieldsForSummary').setValue(fieldsForSummary);
                }
            }
        });
    }

    ngOnDestroy() {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        this.updateFieldNameSubscription.unsubscribe();
        this.events.destroy('updateFieldName');
    }

    childChanged(event: { columns: DTypeCol[]; dtype: DType }) {
        this.hasChanges = true;
        if (event?.columns) {
            this.formService.updateColumns(event.columns);
        }
        if (event?.dtype) {
            this.form = event.dtype;
        }
    }

    async save() {
        const uri = await this.saveChanges();
        if (this.cancelSave){
            return;
        }
        if (this.repo.errorStatus === 'ERROR') {
            this.dialogsService.openErrorDialog({
                message: this.translocoService.translate('requestDTypeToGoLiveDialog.errorInDesign'),
            });
            return;
        }
        if (uri && this.repo.release > 0) {
            this.formService.emitUpdateSidebarEvent();
            this.params = { ...this.params, uri: uri };
            this.router.navigate(['../summary'], {
                relativeTo: this.activatedRoute,
                queryParams: this.params
            });
        } else {
            if (!this.equalSQLTableModel) {
                const user = await firstValueFrom(this.userService.user$);
                if (this.role?.dataVerifier) {
                    const _confirm = await this.confirm(
                        this.translocoService.translate('requestDTypeToGoLiveDialog.title'),
                        this.translocoService.translate('requestDTypeToGoLiveDialog.dataVerifierMessage'),
                        this.translocoService.translate('requestDTypeToGoLiveDialog.dataVerifierConfirm'),
                        this.translocoService.translate('requestDTypeToGoLiveDialog.dataVerifierCancel')
                    );
                    if (_confirm) {
                        const inner: DTypeToGoLiveRequest = {
                            userId: user.userId,
                            requirementsDate: null,
                            observations: null,
                            relativeUrl: this.router.url.replace('design?', 'summary?')
                        };
                        const body: RequestGoLiveBody = {
                            uri: this.uri,
                            dtypeToGoLiveRequest: inner,
                            cancelMail: true
                        };
                        this.dtypeService.flushDTypeToGoLiveRequest(body).pipe(
                            catchError((error) => {
                                this.dialogsService.openErrorDialog({
                                    message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                                });
                                this.loading = false;
                                console.error(error);
                                return throwError(() => error.error);
                            }),
                            map(reply => reply.repository)
                        ).subscribe((repo) => {
                            if (repo) {
                                const form = repo.payload as Form;
                                if (form) {
                                    this.repo = repo;
                                    this.form = form;
                                    this.updateForm(repo);
                                    this._snackbar.open(this.translocoService.translate('formSaved', {
                                        name: form.name
                                    }), this.translocoService.translate('accept'), { duration: 5000 });
                                    this.hasChanges = false;
                                    this.formService.emitUpdateSidebarEvent();
                                    this.params = { ...this.params, uri: this.uri, dataVerifier: true };
                                    this.router.navigate(['../summary'], {
                                        relativeTo: this.activatedRoute,
                                        queryParams: this.params
                                    });
                                    this.loading = false;
                                } else {
                                    this._snackbar.open(this.translocoService.translate('somethingWentWrong'), this.translocoService.translate('accept'), { duration: 5000 });
                                }
                                this.loading = false;
                            }
                        });
                        return;
                    } else {
                        this.requestLive();
                    }
                } else {
                    const confirm = await this.confirmLive(
                        this.translocoService.translate('dtypeTableChangesDetected'),
                        this.translocoService.translate('dTypeTableModelChanges') + this.translocoService.translate('requestLive'),
                        this.translocoService.translate('requestLive'));
                    if (confirm) {
                        this.requestLive();
                    }
                }
            } else if (!this.equalDType) {
                const confirm = await this.confirmLive(
                    this.translocoService.translate('dtypeChangesDetected'),
                    this.translocoService.translate('dTypeNoTableModelChanges') + this.translocoService.translate('updateLive'),
                    this.translocoService.translate('updateLive'));
                if (confirm) {
                    this.updateLive();
                }
            } else {
                this._snackbar.open(this.translocoService.translate('formSaved', {
                    name: this.form.name
                }), this.translocoService.translate('accept'), { duration: 5000 });
            }
        }
    }

    cancel() {
        this.goBack();
    }

    async updateLive() {
        this.loading = true;
        this.formService.updateLive(this.uri).pipe(
            catchError((error) => {
                this.dialogsService.openErrorDialog({
                    message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                });
                this.loading = false;
                console.error(error);
                return throwError(() => error.error);
            }),
            map(reply => reply.repository)
        ).subscribe((repo) => {
            if (repo) {
                const form = repo.payload as Form;
                if (form) {
                    this.repo = repo;
                    this.form = form;
                    this.updateForm(repo);
                    this._snackbar.open(this.translocoService.translate('formSaved', {
                        name: form.name
                    }), this.translocoService.translate('accept'), { duration: 5000 });
                    this.hasChanges = false;
                    this.formService.emitUpdateSidebarEvent();
                    this.params = { ...this.params, uri: this.uri };
                    this.router.navigate(['../summary'], {
                        relativeTo: this.activatedRoute,
                        queryParams: this.params
                    });
                    this.loading = false;
                } else {
                    this._snackbar.open(this.translocoService.translate('somethingWentWrong'), this.translocoService.translate('accept'), { duration: 5000 });
                }
                this.loading = false;
            }
        });
    }

    async requestLive() {
        const user = await firstValueFrom(this.userService.user$);
        const data: RequestGoLiveDialogData = {
            uri: this.uri,
            relativeUrl: this.router.url.replace('design?', 'summary?'),
            userId: user.userId
        };
        this.dialogsService.openRequestGoLiveDataDialog(data).afterClosed().subscribe((repo) => {
            if (repo) {
                const form = repo.payload as Form;
                if (form) {
                    this.repo = repo;
                    this.form = form;
                    this.updateForm(repo);
                    this._snackbar.open(this.translocoService.translate('formSaved', {
                        name: form.name
                    }), this.translocoService.translate('accept'), { duration: 5000 });
                    this.hasChanges = false;
                    this.formService.emitUpdateSidebarEvent();
                    this.params = { ...this.params, uri: this.uri };
                    this.router.navigate(['../summary'], {
                        relativeTo: this.activatedRoute,
                        queryParams: this.params
                    });
                    this.loading = false;
                } else {
                    this._snackbar.open(this.translocoService.translate('somethingWentWrong'), this.translocoService.translate('accept'), { duration: 5000 });
                }
                this.loading = false;
            }
        });
    }

    deleteRepository() {
        this.dtypeService.deleteNextDType(this.uri).subscribe({
            next: (reply) => {
                if (reply?.status === 200) {
                    this._snackbar.open(this.translocoService.translate('deletedDType',
                        { name: this.localize.transform(this.form?.caption) }), this.translocoService.translate('accept'), { duration: 5000 });
                    this.goBack();
                } else {
                    this.dialogsService.openErrorDialog();
                }
            }, error: (err) => {
                console.error(err);
                this.dialogsService.openErrorDialog({
                    message: err?.error?.echo ? this.localize.transform(err?.error?.echo) : undefined,
                });
            }
        });
    }

    changeView() {
        this.databaseViewMode = !this.databaseViewMode;
    }

    changeRepoName() {
        this.dialogsService.openChangeRepoNameDialog(this.form.name).afterClosed().subscribe((_name) => {
            if (_name) {
                this.loading = true;
                const body: ChangeDTypeRepositoryNameBody = {
                    uri: this.repo.uri,
                    name: _name
                };
                this.dtypeService.changeDTypeRepositoryName(body).pipe(
                    catchError((error) => {
                        this.dialogsService.openErrorDialog({
                            message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                        });
                        this.loading = false;
                        console.error(error);
                        return throwError(() => error.error);
                    }),
                    map(reply => reply.repository)
                ).subscribe((repo) => {
                    if (repo) {
                        const form = repo.payload as Form;
                        if (form) {
                            this.repo = repo;
                            this.form = form;
                            this.updateForm(repo);
                            this.params = { ...this.params, uri: this.form.uri };
                            this.router.navigate(['../design'], {
                                relativeTo: this.activatedRoute,
                                queryParams: this.params
                            });
                            this._snackbar.open(this.translocoService.translate('formSaved', {
                                name: form.name
                            }), this.translocoService.translate('accept'), { duration: 5000 });
                            this.hasChanges = false;
                            this.formService.emitUpdateSidebarEvent();
                            this.loading = false;
                        } else {
                            this._snackbar.open(this.translocoService.translate('somethingWentWrong'), this.translocoService.translate('accept'), { duration: 5000 });
                        }
                        this.loading = false;
                    }
                });
            }
        });
    }

    async discardDesign() {
        this.loading = true;
        const confirm = await this.openConfirmDialogWithCountdown(
            this.translocoService.translate('dtypeDiscardDesign.title'),
            this.translocoService.translate('dtypeDiscardDesign.message'),
            this.translocoService.translate('dtypeDiscardDesign.countdownMessage'));
        if (confirm) {
            this.dtypeService.discardDesign({uri: this.uri}).pipe(
                catchError((error) => {
                    this.dialogsService.openErrorDialog({
                        message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                    });
                    this.loading = false;
                    console.error(error);
                    return throwError(() => error.error);
                }),
                map(reply => reply.repository)
            ).subscribe((repo) => {
                if (repo) {
                    const form = repo.payload as Form;
                    if (form) {
                        this.repo = repo;
                        this.form = form;
                        this.updateForm(repo);
                        this.formService.updateColumns(this.form.column);
                        this.hasChanges = false;
                        this._snackbar.open(this.translocoService.translate('dtypeDiscardDesign.success'), this.translocoService.translate('accept'), { duration: 5000 });
                        this.formService.emitUpdateSidebarEvent();
                        this._changeDetectorRef.detectChanges();
                        this.loading = false;
                    } else {
                        this._snackbar.open(this.translocoService.translate('dtypeDiscardDesign.abort'), this.translocoService.translate('accept'), { duration: 5000 });
                    }
                    this.loading = false;
                }
            });
        } else {
            this._snackbar.open(this.translocoService.translate('dtypeDiscardDesign.abort'), this.translocoService.translate('accept'), { duration: 5000 });
        }
        this.loading = false;
    }

    private hasPermissionToDesignRepository() {
        return this.projectsService.hasPermissionToDesignRepository(this.role, 'form');
    }

    private async saveChanges() {
        this.cancelSave = false;
        const confirm = await this.confirm(this.translocoService.translate('warning'), this.translocoService.translate('saveFormConfirm'));
        if (confirm) {
            this.loading = true;
            this.dtypeForm.control.markAllAsTouched();
            if (!this.dtypeForm.control.valid) {
                this.loading = false;
                this.selectedTab = 0;
                this.dialogsService.openErrorDialog({
                    message: this.translocoService.translate('errorsInForm')
                });
                this.cancelSave = true;
                return;
            }
            try {
                Object.assign(this.form, this.dtypeForm.control.getRawValue());
                if (this.isNew) {
                    delete this.form.uri;
                }
                const response = await firstValueFrom(this.formService.flushForm(this.form, this.dtypeLocator));
                const form = response?.repository?.payload as Form;
                if (response?.equalDType != null) {
                    this.equalDType = response.equalDType;
                }
                if (response?.equalSQLTableModel != null) {
                    this.equalSQLTableModel = response.equalSQLTableModel;
                }
                if (form) {
                    this.repo = response?.repository;
                    this.form = form;
                    this.updateForm(response?.repository);
                    this._snackbar.open(this.translocoService.translate('formSaved', { name: form.name }), this.translocoService.translate('accept'), { duration: 5000 });
                    this.hasChanges = false;
                    if (this.isNew) {
                        this.router.navigate(['../design'], { relativeTo: this.activatedRoute, queryParams: { ...this.params, uri: form.uri } });
                    }
                    this.loading = false;
                    return form.uri;
                } else {
                    this._snackbar.open(this.translocoService.translate('somethingWentWrong'), this.translocoService.translate('accept'), { duration: 5000 });
                }
                this.loading = false;
                return;
            } catch (error) {
                this.loading = false;
                this.dialogsService.openErrorDialog({
                    message: error?.echo && Object.keys(error.echo).length ? this.localize.transform(error.echo) : undefined
                });
                this.cancelSave = true;
            }
        } else {
            this.cancelSave = true;
        }
    }

    private updateForm(repo: DTypeRepository) {
        let control: AbstractControl;
        for (const key of Object.keys(repo.payload)) {
            control = this.dtypeForm.control.get(key) as any;
            if (control) {
                control.patchValue(repo.payload[key]);
            }
        }
    }

    private goBack() {
        this.router.navigate(['../'], {
            queryParams: {
                ou: '01',
                marca_code: this.myLocator.marca_code,
                cliente_code: this.myLocator.cliente_code,
                projectId: this.myLocator.projectId,
                serviceId: this.myLocator.serviceId
            },
            relativeTo: this.activatedRoute
        });
    }

    private confirm(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.translocoService.translate('unsavedChanges'),
            message: message ? message : this.translocoService.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.translocoService.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.translocoService.translate('cancel')
                }
            }
        });

        return firstValueFrom(dialogRef.afterClosed().pipe(map(result => result === 'confirmed')));
    }

    private confirmLive(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.translocoService.translate('unsavedChanges'),
            message: message ? message : this.translocoService.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.translocoService.translate('accept'),
                    color: 'primary'
                },
                cancel: {
                    label: cancel ? cancel : this.translocoService.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe(result => resolve(result === 'confirmed'));
        });
    }

    private openConfirmDialogWithCountdown(title: string, message: string, countdownMessage: string) {
        const dialog = this.dialogsService.openConfirmWithCountdownDialog({
            title,
            message,
            countdownMessage,
            seconds: 10,
            actions: {
                confirm: {
                    label: this.translocoService.translate('confirm')
                },
                cancel: {
                    label: this.translocoService.translate('revert'),
                }
            },
        });

        return new Promise<boolean>((resolve) => {
            dialog.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }

}
